/**
 * Deep-link URL builders and parsers for shared profiles, groups, and posts.
 *
 * Outbound share/copy uses SHARE_PROFILE_URL for profiles, SHARE_GROUP_URL for groups,
 * and SHARE_POST_URL for posts.
 * Inbound handling accepts both standard HTTPS links (Universal Links / App Links)
 * and the custom scheme `weightlossbuddy://`.
 */
import {
  SHARE_POST_URL,
  SHARE_GROUP_URL,
  SHARE_PROFILE_URL,
  buildSharePostUrl,
  buildShareGroupUrl,
  buildShareProfileUrl,
} from '../config/env';

/** Custom URL scheme registered on Android + iOS. */
export const APP_LINK_SCHEME = 'weightlossbuddy';

/** Native app IDs for stores and system integrations. */
export const ANDROID_PACKAGE_NAME = 'com.weightlossbuddy';
export const IOS_BUNDLE_ID = 'com.wlb.app';
export const APP_STORE_ID = '1500366143';
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_NAME}`;
export const APP_STORE_URL = `https://apps.apple.com/app/weight-loss-buddy/id${APP_STORE_ID}`;

export type DeepLinkTarget =
  | { type: 'profile'; userId: string }
  | { type: 'group'; groupId: string }
  | { type: 'post'; postId: string };

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

const INVALID_ID_TOKENS = new Set(['', 'undefined', 'null', 'nan']);

const isValidTargetId = (id: unknown): id is string => {
  if (id == null) return false;
  const trimmed = String(id).trim();
  return Boolean(trimmed && !INVALID_ID_TOKENS.has(trimmed.toLowerCase()));
};

const sanitizeTargetId = (rawId: string): string | null => {
  try {
    const decoded = decodeURIComponent(rawId.trim());
    return isValidTargetId(decoded) ? decoded : null;
  } catch {
    return isValidTargetId(rawId) ? rawId.trim() : null;
  }
};

const pathFromHttpsBase = (baseUrl: string): { host: string; prefix: string } | null => {
  try {
    const parsed = new URL(stripTrailingSlash(baseUrl));
    const prefix = parsed.pathname.replace(/\/+$/, '') || '';
    return { host: parsed.host.toLowerCase(), prefix };
  } catch {
    return null;
  }
};

/** Prefixes React Navigation / Linking should recognize. */
export const getDeepLinkPrefixes = (): string[] => {
  const prefixes = new Set<string>([
    `${APP_LINK_SCHEME}://`,
    'https://weightlossbuddy.com',
    'https://demo.weightlossbuddy.com',
    'https://dev.weightlossbuddy.com',
  ]);
  for (const base of [SHARE_POST_URL, SHARE_GROUP_URL, SHARE_PROFILE_URL]) {
    try {
      if (base) {
        const parsed = new URL(stripTrailingSlash(base));
        prefixes.add(`${parsed.protocol}//${parsed.host}`);
      }
    } catch {
      // ignore invalid env URLs
    }
  }
  return Array.from(prefixes);
};

export { buildSharePostUrl, buildShareGroupUrl, buildShareProfileUrl };

/**
 * Builds a custom scheme deep link (e.g. `weightlossbuddy://profile/{userId}`).
 */
export const buildCustomSchemeUrl = (target: DeepLinkTarget): string => {
  switch (target.type) {
    case 'profile':
      return `${APP_LINK_SCHEME}://profile/${encodeURIComponent(target.userId)}`;
    case 'group':
      return `${APP_LINK_SCHEME}://group/${encodeURIComponent(target.groupId)}`;
    case 'post':
      return `${APP_LINK_SCHEME}://post/${encodeURIComponent(target.postId)}`;
    default:
      return `${APP_LINK_SCHEME}://`;
  }
};

/**
 * Returns store URL for the given platform.
 */
export const getStoreUrl = (platform: 'android' | 'ios' | 'unknown' | string): string | null => {
  const lower = platform.toLowerCase();
  if (lower.includes('android')) {
    return PLAY_STORE_URL;
  }
  if (lower.includes('ios') || lower.includes('iphone') || lower.includes('ipad') || lower.includes('ipod')) {
    return APP_STORE_URL;
  }
  return null;
};

/**
 * Parse an inbound URL into a profile / group / post target.
 * Supports:
 * - weightlossbuddy://profile/{userId}
 * - weightlossbuddy://group/{groupId}
 * - weightlossbuddy://groups/{groupId}
 * - weightlossbuddy://post/{postId}
 * - weightlossbuddy://u/{userId}
 * - {SHARE_PROFILE_URL}/{userId}
 * - {SHARE_GROUP_URL}/{groupId}
 * - {SHARE_POST_URL}/{postId}
 * - https://{host}/profile/{userId}
 * - https://{host}/group/{groupId}
 * - https://{host}/groups/{groupId}
 * - https://{host}/profile/others/{userId}
 */
export const parseDeepLink = (rawUrl?: string | null): DeepLinkTarget | null => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  const host = url.host.toLowerCase();
  const pathParts = url.pathname
    .split('/')
    .map(p => p.trim())
    .filter(Boolean);

  // Custom scheme:
  // weightlossbuddy://profile/123  → host=profile, path=/123
  // weightlossbuddy:///profile/123 → host='', path=/profile/123
  const protocol = url.protocol.toLowerCase();
  if (protocol === `${APP_LINK_SCHEME.toLowerCase()}:`) {
    const segments = host && host !== '' ? [host, ...pathParts] : pathParts;
    if (segments.length >= 2) {
      const kind = segments[0].toLowerCase();
      const rawId = segments[segments.length - 1];
      const validId = sanitizeTargetId(rawId);
      if (!validId) return null;

      if (kind === 'profile' || kind === 'u') {
        return { type: 'profile', userId: validId };
      }
      if (kind === 'group' || kind === 'groups') {
        return { type: 'group', groupId: validId };
      }
      if (kind === 'post') {
        return { type: 'post', postId: validId };
      }
    }
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }

  const profileBase = pathFromHttpsBase(SHARE_PROFILE_URL);
  const groupBase = pathFromHttpsBase(SHARE_GROUP_URL);
  const postBase = pathFromHttpsBase(SHARE_POST_URL);

  // Exact matching against configured base URLs
  if (profileBase && host === profileBase.host) {
    const prefixParts = profileBase.prefix.split('/').filter(Boolean);
    if (
      pathParts.length === prefixParts.length + 1 &&
      prefixParts.every((p, i) => pathParts[i]?.toLowerCase() === p.toLowerCase())
    ) {
      const validId = sanitizeTargetId(pathParts[pathParts.length - 1]);
      if (validId) return { type: 'profile', userId: validId };
    }
  }

  if (groupBase && host === groupBase.host) {
    const prefixParts = groupBase.prefix.split('/').filter(Boolean);
    if (
      pathParts.length === prefixParts.length + 1 &&
      prefixParts.every((p, i) => pathParts[i]?.toLowerCase() === p.toLowerCase())
    ) {
      const validId = sanitizeTargetId(pathParts[pathParts.length - 1]);
      if (validId) return { type: 'group', groupId: validId };
    }
  }

  if (postBase && host === postBase.host) {
    const prefixParts = postBase.prefix.split('/').filter(Boolean);
    if (
      pathParts.length === prefixParts.length + 1 &&
      prefixParts.every((p, i) => pathParts[i]?.toLowerCase() === p.toLowerCase())
    ) {
      const validId = sanitizeTargetId(pathParts[pathParts.length - 1]);
      if (validId) return { type: 'post', postId: validId };
    }
  }

  // Handle `/profile/others/:userId`
  if (
    pathParts.length >= 3 &&
    pathParts[0].toLowerCase() === 'profile' &&
    pathParts[1].toLowerCase() === 'others'
  ) {
    const validId = sanitizeTargetId(pathParts[2]);
    if (validId) return { type: 'profile', userId: validId };
  }

  // Generic fallback: /profile/:id, /group/:id, /groups/:id, /post/:id, /u/:id on any host
  if (pathParts.length >= 2) {
    const kind = pathParts[pathParts.length - 2].toLowerCase();
    const validId = sanitizeTargetId(pathParts[pathParts.length - 1]);
    if (validId) {
      if (kind === 'profile' || kind === 'u') {
        return { type: 'profile', userId: validId };
      }
      if (kind === 'group' || kind === 'groups') {
        return { type: 'group', groupId: validId };
      }
      if (kind === 'post') {
        return { type: 'post', postId: validId };
      }
    }
  }

  return null;
};
